// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useState, useEffect } from 'react';
import { Modal, TextArea as FinTextArea, InputLabel, StatusBanner } from '@loa/ui-components';

const MAX_REASON_LENGTH = 500;

type VoidRequestModalProps = Readonly<{
  open: boolean;
  /** Request type label used in the body copy (e.g. "LOA" / "CoA"). */
  requestType?: string;
  onClose: () => void;
  /**
   * Called with the trimmed reason when the advisor confirms. Should perform
   * the void + refresh. Throwing keeps the modal open and surfaces the error.
   */
  onConfirm: (reason: string) => Promise<void>;
}>;

/**
 * Red (destructive) confirmation modal that replaces the native
 * `prompt('Void reason:')` for voiding a Profile Request on the Requests tab.
 * Keeps the existing logic — a reason is required before the void is sent.
 */
export function VoidRequestModal({ open, requestType, onClose, onConfirm }: VoidRequestModalProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const trimmedReason = reason.trim();
  const isConfirmDisabled = trimmedReason.length === 0 || submitting;

  const handleConfirm = async () => {
    if (trimmedReason.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(trimmedReason);
      onClose();
    } catch {
      setError('Failed to delete request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_REASON_LENGTH) {
      setReason(text);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const typeLabel = requestType ? requestType.toLowerCase() : 'request';

  return (
    <Modal open={open} onClose={handleClose} size="medium" variant="warning">
      <Modal.Header icon="trash" title="Delete request" onClose={handleClose} />
      <Modal.Content>
        <div className="flex flex-col gap-lg">
          <div className="text-[#000] font-sans text-body-02 font-medium leading-normal">
            You are about to delete this {typeLabel}. Please provide a reason for this action.
          </div>

          <div>
            <InputLabel htmlFor="voidReason" required>Reason</InputLabel>
            <FinTextArea
              id="voidReason"
              placeholder="Describe the reason for deleting this request..."
              value={reason}
              onChange={handleReasonChange}
              disabled={submitting}
              rows={4}
              fluid
            />
          </div>

          {error && (
            <StatusBanner
              variant="error"
              message={error}
              visible
              position="inline"
              dismissible={false}
            />
          )}
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: handleClose, disabled: submitting }}
        primaryAction={{
          label: 'Delete',
          colour: 'red',
          onClick: () => { void handleConfirm(); },
          disabled: isConfirmDisabled,
          loading: submitting,
        }}
      />
    </Modal>
  );
}
